// AppLightbox — image / video preview overlay. Migrated 2026-06-01
// from a per-state-field shell-root mount to @symbo.ls/windows. Body-
// only now: backdrop, click-to-dismiss, ESC-to-close are owned by
// ModalLayer. Opened via `el.call('openLightbox', { url, mime, name })`
// — the helper writes the descriptor to `root.lightbox` (preserved as
// data carrier so this component's reactive reads stay unchanged) and
// pushes a custom-template layer onto ModalRoot.
//
// The body fills the layer (90vw / 85vh constraint on the media element)
// and includes a centred filename caption. The CloseBtn at top-right
// stops propagation so it doesn't trigger backdrop dismiss.
export const AppLightbox = {
  flow: 'y',
  align: 'center center',
  // WS-MODAL-X-HORIZONTAL-OVERFLOW-1: fill the custom-template slot instead
  // of forcing 90vw inside it. The window itself is sized 90vw by the
  // `openLightbox` layer descriptor, so a 90vw body inside it overflowed the
  // window by its padding and pushed the absolute CloseBtn past the right
  // viewport edge (ModalWindow is `overflow: 'visible'`, so nothing clipped
  // or scrolled it back).
  width: '100%',
  maxWidth: '100%',
  height: '85vh',
  position: 'relative',

  // THE house close X (brand CloseButton — F2, one X for every panel);
  // geometry + glyph inherited, only the scrim ink below is local.
  CloseBtn: {
    extends: 'CloseButton',
    position: 'absolute',
    top: 'A',
    right: 'A',
    // Deliberately FIXED: floats over arbitrary preview media (photo/video)
    // on ModalLayer's own fixed 'black.45' scrim (@symbo.ls/windows —
    // ModalLayer.js) — same "scrim over arbitrary imagery" contract as a
    // lightbox chrome everywhere else; must not flip with site theme.
    background: 'white.15',
    color: 'white',
    ':hover': { background: 'white.3', color: 'white' },
    onClick: (e, el) => {
      e.stopPropagation()
      el.call('closeLightbox')
    }
  },

  Image: {
    tag: 'img',
    hide: (el, s) => !(s.root.lightbox?.mime || '').startsWith('image/'),
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    round: 'radiusControl',
    cursor: 'default',
    // No `|| ''` — the lightbox is mounted on every page and closed most of
    // the time, and an empty `src` is a REAL request for the document URL
    // (2 failed requests per mount, served catalog 2026-09-20). smbls
    // removes the attribute when the factory answers undefined; `hide:`
    // above is CSS and never stopped the request. See
    // __tests__/lightboxEmptyMediaSrc.test.js.
    src: (el, s) => s.root.lightbox?.url || undefined,
    alt: (el, s) => s.root.lightbox?.name || 'preview',
    onClick: (e) => e.stopPropagation()
  },

  Video: {
    tag: 'video',
    hide: (el, s) => !(s.root.lightbox?.mime || '').startsWith('video/'),
    maxWidth: '100%',
    maxHeight: '100%',
    round: 'radiusControl',
    cursor: 'default',
    // Same as the Image slot above — an empty `src` on a media element is
    // the same document-URL request, and it ends in a decode error on a
    // resource that is an HTML page.
    src: (el, s) => s.root.lightbox?.url || undefined,
    controls: 'true',
    autoplay: 'true',
    onClick: (e) => e.stopPropagation()
  },

  FileName: {
    position: 'absolute',
    bottom: '-A',
    left: '50%',
    transform: 'translateX(-50%)',
    // Deliberately FIXED — same lightbox-chrome-over-arbitrary-media
    // contract as CloseBtn above.
    color: 'white.7',
    fontSize: 'Z2',
    textAlign: 'center',
    pointerEvents: 'none',
    text: (el, s) => s.root.lightbox?.name || ''
  }
}
